import {Component, Input, OnInit, EventEmitter, Output, SimpleChange, OnChanges} from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import {Contact} from "./contact";
import { OptionService } from "../option/option.service";
import {Donation} from "../donation/donation";
import {GlobalValidators} from "../validate/validate.globalvalidators";
import {HttpResponseBase} from "@angular/common/http";
import {DonationService} from "../donation/donation.service";
import {UrlService} from "../url/url.service";
import {TranslateService} from "@ngx-translate/core";
import {ElementService} from "../service/element.service";
import {GenderService} from "../service/gender.service";
import { EmailService } from 'service/email.service';

@Component({
    selector: 'contact',
    templateUrl: './contact.component.html'
})
export class ContactComponent implements OnInit, OnChanges {
    /**
     * List of alle contact fields to avoid conflicts with other formfields from other components if the same form is used.
     */
    public static readonly allContactFields = ['salutation', 'title', 'firstname', 'lastname', 'postal_code', 'city', 'street', 'address_additional_info', 'telephone', 'company', 'country', 'birthday', 'email'];

    /**
     * Curretn donation
     */
    @Input() donation: Donation;

    /**
     * Formgroup for validating
     */
    @Input() form: FormGroup;

    /**
     * Handling for the contact data
     */
    @Input() contact: Contact;

    /**
     * The current step of the application
     */
    @Input() step: string;

    /**
     * Size of the users browser
     */
    @Input() size: string;

    /**
     * version that is visible (widget, page, form)
     */
    @Input() version: string;

    /**
     * The users country to trigger changes on this value
     */
    @Input() country: string;

    /**
     * Should the newsletter be hidden in the contact component
     */
    @Input() hideNewsletterInContact = false;

    /**
     * Emitter for changing the step
     * @type {EventEmitter}
     */
    @Output() stepChange: EventEmitter<string> = new EventEmitter();

    /**
     * whether to show the salutation or not
     */
    @Input() salutationShown = false;

    /**
     * Should we ask for contact data
     * @type {any}
     */
    hasPostInfo: boolean = null;

    /**
     * should we ask for donation receipt
     * @type {any}
     */
    hasDonationReceipt: boolean = null;

    /**
     * should we ask for newsletter registration
     * @type {any}
     */
    hasNewsletterRegistration: boolean = null;


    /**
     * Textkey for the main headline of the component
     * @type {any}
     */
    headlineTextKey: string = null;

    /**
     * Textkey for the Teaser
     * @type {any}
     */
    teaserTextKey: string = null;

    /**
     * List of fields to be excluded from the form
     * @type {Array}
     */
    excludedFields: string[] = [];

    /**
     * Holds if the contact data is requested mandatory
     * @type {boolean}
     */
    contactMandatory: boolean = false;

    /**
     * Indicates if we want to show a error message on the checkboxes
     * @type {boolean}
     */
    showCheckboxError: boolean = false;

    /**
     * Should the firstname/lastname/salutation field be mandatory when the user wants the newsletter
     */
    hasNewsletterNamerequest: boolean = false;

    /**
     * holds all mandatory contact fields.
     */
    mandatoryFields: any;

    /**
     * toggle if the newsletter subscription is finished or not
     */
    newsletterSubscriptionFinished: boolean = false;

    /**
     * Holds the config for additional fields
     */
    addtionalFields: any = [];

    /**
     * Flag if the additional fields should be saved on the donation.
     */
    addtionalFieldsOnDonation = false;

    /**
     * indicates if the user a is company
     * using getter and setter to update the validators
     */
    _isCompany: boolean = false;

    /**
     * flag to show the address info field
     */
    showAddressInfoField: boolean = false;

    get isCompany(): boolean {
        return this._isCompany;
    }
    
    set isCompany(value: boolean) {
        this._isCompany = value;

        // toggle validators for company
        if (value) {
            this.form.get('company').setValidators([Validators.required]);
        } else {
            this.form.get('company').clearValidators();
            // clears the input data
            this.form.get('company').reset();
            this.contact.data.company = null;
        }

        // revalidate the form
        this.form.get('company').updateValueAndValidity();
    }

    /**
     * The constructor
     * @param optionService
     * @param donationService
     * @param urlService
     * @param translate
     * @param elementService
     * @param genderService
     * @param emailService
     */
    constructor(protected optionService: OptionService, protected donationService: DonationService, protected urlService: UrlService, private translate: TranslateService,
                private elementService: ElementService, private genderService: GenderService, private emailService: EmailService) {
        // get mandatory fields and set validators
        this.mandatoryFields = this.optionService.getOption('mandatory_contact_fields').split(",");
    }

    /**
     * Check for changes on the input values
     * @param changes
     */
    ngOnChanges(changes: {[propertyName: string]: SimpleChange}): void {
        if(changes["step"] && changes["step"].currentValue == "contact-mandatory" && changes["step"].previousValue == "start") {
            // if we come from the start step and should show the contact-mandatory form, preset the email
            this.showForm();
        }

        if(changes["step"] && changes["step"].currentValue == "thankyou" && changes["step"].previousValue == "contact") {
            // back from the contact form, update the validators
            this.updateValidators();
        }

        if(changes["step"] && (changes["step"].currentValue == "thankyou" || changes["step"].currentValue == "contact-mandatory")) {
            // preset email, this could lead to a raise condition because the fields are not yet in the form. Therefor we have the same in the init() function
            this.presetEmail();
        }

        if(changes["step"] && changes["step"].currentValue == "contact" && changes["step"].previousValue == "thankyou") {
            // coming from the thankyou step
            // we check if the newsletter subscription is already finished (from newsletter component)
            this.newsletterSubscriptionFinished = this.contact.wants_newsletter;
            // then we preselect the checkbox if only one is available
            this.preselectCheckbox();
            // preset salutation, this could lead to a raise condition because the fields are not yet in the form. Therefor we have the same in the init() function
            this.presetSalutation();
        }

        // Update country preset and country validator if the country has changed
        if(changes["country"] && this.form.controls['country']) {
            // Update user Country
            this.form.controls['country'].setValue(this.contact.data.country);
            this.form.controls['country'].updateValueAndValidity();

            // init validator of the country
            this.updateCountry();
        }
    }

    /**
     * Function is called every time a checkbox changed
     */
    updateValidators(): void {
        // disable all
        for(let name of ContactComponent.allContactFields) {
            if(name != 'email' && name != 'extra' && typeof this.form.controls[name] !== "undefined") {
                (<FormControl>this.form.controls[name]).disable({emitEvent: true});
            }
        }

        let enableForms = this.getFieldsToEnable();

        // enable all fields that are needed but only the fields that are not excluded
        for(let i=0; i<enableForms.length; i++) {
            if(this.excludedFields.indexOf(enableForms[i]) == -1 && typeof this.form.controls[enableForms[i]] !== "undefined") {
                this.form.controls[enableForms[i]].enable({emitEvent: true});
            }
        }

        // reset the error message for the checkboxes
        if(this.isNothingChoosen() == false) {
            this.showCheckboxError = false;
        }
    }

    /**
     * Returns the list of fields that have to be enabled in the current status of the form.
     */
    getFieldsToEnable(): any {
        let enableForms: any = [];
        if(this.hasNewsletterNamerequest === true) {
            // name and salutation should be request when asking for the newsletter
            enableForms = ['firstname', 'lastname'];
        }
        if(this.contactMandatory == true || this.step === 'contact') {
            // contact data is mandatory in general, or postinfo or donation receipt wanted
            enableForms = ContactComponent.allContactFields;
        }
        return enableForms;
    }

    /**
     * is called when initialising the component
     * @constructor
     */
    ngOnInit(): void {
        // check if the contact data should be requested manually
        this.contactMandatory = ContactComponent.isContactMandatory();

        this.hasNewsletterNamerequest = this.optionService.getOption('has_newsletter_namerequest') === true;

        this.addtionalFields = this.optionService.getOption('contact_configuration');
        this.addtionalFieldsOnDonation = this.optionService.getOption('has_donation_contact_configuration') === true;

        // add email field as mandatory field because we always have this
        this.form.addControl('email', new FormControl('', [Validators.required, GlobalValidators.validateEmail]));

        // add the three checkboxes also to the form, even if they are not validated
        this.form.addControl('wants_newsletter', new FormControl(''));
        this.form.addControl('wants_donation_receipt', new FormControl(''));
        this.form.addControl('wants_postinfo', new FormControl(''));

        for(let field of ContactComponent.allContactFields) {
            // set validators for all fields exept email based on the settings for mandatory fields
            if(field === 'street') {
                // the street field needs an extra validation for the housenumber
                this.form.addControl(field, new FormControl('', this.isMandatory(field)? [Validators.required, GlobalValidators.validateAddress] : null));
            } else if (field === 'birthday') {
                this.form.addControl(field, new FormControl('', this.isMandatory(field) ? [Validators.required, GlobalValidators.validateBirthday] : [GlobalValidators.validateBirthday]));
            }
            else if(field != 'email') {
                this.form.addControl(field, new FormControl('', this.isMandatory(field)? [Validators.required] : null));
            }

            // preset values from the URL
            let fieldValue = this.urlService.findGetParameter("tw_" + field);
            if(fieldValue) {
                this.contact.data[field] = fieldValue;
                this.form.controls[field].setValue(this.contact.data[field]);
            }
        }

        let excludedFields = this.optionService.getOption("exclude_contact_fields");
        if(excludedFields) {
            this.excludedFields = excludedFields.split(",");
        }

        // check which items should be visible
        this.hasPostInfo = this.optionService.getOption("has_postinfo_registration");
        this.hasDonationReceipt = this.optionService.getOption("has_donation_receipt");
        this.hasNewsletterRegistration = this.optionService.getOption("has_newsletter_registration");

        // if the donation amount is 0, we don't need the donation receipt
        // we also need to clear any preexisting values
        if (this.donation.amount === 0 && this.donation.extraAmount === 0) {
            this.hasDonationReceipt = false;
            this.contact.wants_donation_receipt = false;
            this.form.controls['wants_donation_receipt'].setValue(false);
        }

        this.updateValidators();

        this.updateTextkeys()

        if(this.step == "contact-mandatory" || (this.contactMandatory && this.version == "form" && this.step == "start")) {
            this.showForm();
        }

        if (this.contact.data.country && this.form.controls['country']) {
          // preset field country
          this.form.controls['country'].setValue(this.contact.data.country);
        }
        this.updateCountry();

        // show salutation initially, if requested
        if (this.salutationShown === true) {
          this.showSalutation();
        }

        // preset email
        this.presetEmail();
        // preset salutation
        this.presetSalutation();

        // determine if we need to show the company field
        if (!this.isMandatory('company') && this.contact.data.company) {
            this.isCompany = true;
        }

        if (this.isMandatory('address_additional_info')) {
          this.showAddressInfoField = true;
        }
    }

    /**
     * tries to preset the meial field from the contact data that was maybe given before.
     */
    presetEmail(): void {
        if (!this.contact.data.email && this.form.controls['email']) {
            this.contact.data.email = this.donation.userEmail;
            this.form.controls['email'].setValue(this.donation.userEmail);
        }
    }

    /**
     * Set the textkeys for headline and teaser of the contact form.
     */
    updateTextkeys(): void {
        this.headlineTextKey = "contact.headline.default";
        this.teaserTextKey = "contact.teaser.default";
        let checkboxCounter = 0;
        if(this.hasPostInfo) {
            checkboxCounter++;
        }
        if(this.hasDonationReceipt) {
            checkboxCounter++;
        }

        if(checkboxCounter == 1 && this.contactMandatory == false) {
            // special case: Only one checkbox available, show special headline and teaser
            switch(true) {
                case this.hasPostInfo:
                    this.headlineTextKey = "contact.headline.postinfo";
                    this.teaserTextKey = "contact.teaser.postinfo";
                    break;
                case this.hasDonationReceipt:
                    this.headlineTextKey = "contact.headline.donationreceipt";
                    this.teaserTextKey = "contact.teaser.donationreceipt";
                    break;
            }
        }
        else if(this.contactMandatory == true) {
            // if the contact data is mandatory for the user show checkboxes on top and show special text
            this.headlineTextKey = "contact.headline.mandatory";
        }
    }

    /**
     * Returns the textkey for the teaser
     * @returns {string}
     */
    getTeaserTextkey(): string {
        return this.teaserTextKey;
    }

    /**
     * Returns if a field should be visible or not
     * @param name
     * @returns {boolean}
     */
    showField(name: string): boolean {
        return this.excludedFields.indexOf(name) == -1;
    }

    /**
     * Returns if a field is mandatory.
     * @param field
     */
    isMandatory(field: string): boolean {
        return this.mandatoryFields.indexOf(field) !== -1;
    }

    /**
     * Decide if the component is visible or not
     * @returns {boolean}
     */
    showComponent(): boolean {
        if (ContactComponent.hasActiveCheckboxes() || ContactComponent.isContactMandatory() === true) {
            if (ContactComponent.isContactMandatory() === true) {
                if (this.version === "form") {
                    return this.step === "start";
                }
                else {
                    return ["contact-mandatory", "contact-mandatory-finished"].indexOf(this.step) !== -1;
                }
            }
            else {
                return ["thankyou", "contact"].indexOf(this.step) !== -1;
            }
        }
        return false;
    }

    /**
     * Returns if there are Checkboxes for the contact component available or not
     * @returns {any}
     */
    public static hasActiveCheckboxes(): boolean {
        let hasPostInfo = OptionService.getOptionStatic("has_postinfo_registration");
        let hasDonationReceipt = OptionService.getOptionStatic("has_donation_receipt");
        let hasNewsletterRegistration = OptionService.getOptionStatic("has_newsletter_registration");

        return (hasDonationReceipt || hasPostInfo || hasNewsletterRegistration);
    }

    /**
     * Returns if the contact form is requested mandatory
     * @returns {any}
     */
    public static isContactMandatory(): boolean {
        return OptionService.getOptionStatic("has_contact_mandatory");
    }

    /**
     * Returns if nothing is choosen
     *
     * @returns {boolean}
     */
    isNothingChoosen(): boolean {
        return this.contact.wants_donation_receipt == false &&
            (this.contact.wants_newsletter == false || this.newsletterSubscriptionFinished === true) &&
            this.contact.wants_postinfo == false;
    }

    /**
     * Returns if a field is invalid
     * @param field
     * @returns {boolean}
     */
    isFieldInvalid(field: string): boolean {
        return this.form.controls[field].touched == true && this.form.controls[field].invalid == true;
    }

    /**
     * Returns the label textkey of a field, depending of it is valid or not
     * @param field
     * @returns {string}
     */
    getLabelKeyForField(field: string): string {
        if(field === 'street' && this.isFieldInvalid(field) && this.form.get('street').errors.hasOwnProperty(GlobalValidators.STREET_MISSING_NUMBER_INDEX) === true) {
            // special case: User has no number in its given address
            return 'contact.street.label.error.number';
        }
        return this.isFieldInvalid(field)? 'contact.' + field + '.label.error' : 'contact.' + field + '.label';
    }

    /**
     * function is called, when the label of the street field is clicked
     */
    onStreetLabelClick(): void {
        if(this.isFieldInvalid('street') === true) {
            this.form.get('street').setValidators([Validators.required]);
            this.form.get('street').updateValueAndValidity();
        }
    }

    /**
     * Shows the contact form
     */
    showForm(): void {
        // check if we have the users country, if not get it from the webservice
        if(this.donation.userCountry && this.contact.data.country === null) {
            this.contact.data.country = this.donation.userCountry;
            this.form.controls['country'].setValue(this.donation.userCountry);

            // init validator of the country
            this.updateCountry();
        }

        let oldstep = this.step;

        if(this.contactMandatory == true) {
            this.step = this.version == "form"? "start" : "contact-mandatory";
        }
        else {
            this.step = "contact";
        }

        if(oldstep != this.step) {
            this.stepChange.emit(this.step);
        }

        this.updateValidators();
    }

    /**
     * Returns if the contact form should be visible or not
     * @returns {boolean}
     */
    isFormVisible(): boolean {
        return (this.version === "form" && this.step === "start") ||
            this.step === "contact" ||
            this.step === "contact-mandatory" ||
            this.step === "contact-mandatory-finished";
    }

    /**
     * Submits the form and sends the data to the server if the form is valid
     *
     */
    submitForm(): void {
        this.showCheckboxError = false;

        // special case: contact data is not mandatory and the user has not checked any checkbox
        if(this.contactMandatory == false && this.isNothingChoosen()) {
            this.showCheckboxError = true;
            setTimeout(() => this.elementService.scrollToElement(document.querySelector('label.error')), 0);
            return;
        }

        if(this.form.valid) {
            // Update the transaction
            let that = this;
            this.donationService.updateDonation(this.contact, this.donation.transactionGuid).subscribe(
                function (res: HttpResponseBase) {
                    that.step = "end";
                    that.stepChange.emit(that.step);
                }
            );
        }
        else {
            // mark fields as invalid
            for(let name in this.form.controls) {
                (<FormControl>this.form.controls[name]).markAsTouched({ onlySelf: true });
            }

            // scroll to the first error message
            setTimeout(() => this.elementService.scrollToElement(document.querySelector('label.error')), 0);
        }
    }

    /**
     * extra submit method for the single newsletter form
     */
    submitNewsletter(): void {
        if(this.form.valid) {
            this.contact.wants_newsletter = true;
            // update the transaction
            this.donationService.updateDonation(this.contact, this.donation.transactionGuid).subscribe(result => {
                if(result && result.result === 1) {
                    this.newsletterSubscriptionFinished = true;
                }
            });
        }
        else {
            for(let name in this.form.controls) {
                (<FormControl>this.form.controls[name]).markAsTouched({ onlySelf: true });
            }
        }
    }

    /**
     * Function is triggered every time the country is changed by the user
     */
    updateCountry(): void {
        if(this.contact.data.country && this.contact.data.country.toLowerCase() == "de" && this.isMandatory('postal_code')) {
            // postal_code is mandatory
            this.form.controls['postal_code'].setValidators([GlobalValidators.validateGermanPostalCode]);
            this.form.controls['postal_code'].updateValueAndValidity();
        }
        else {
            // not DE, postal_code is not mandatory
            if(this.isMandatory('postal_code')) {
                this.form.controls['postal_code'].setValidators([Validators.required]);
            }
            else {
                this.form.controls['postal_code'].setValidators([]);
            }
            this.form.controls['postal_code'].updateValueAndValidity();
        }
    }

    /**
     * Sets the salutation based on the gender determined from the firstname
     */
    presetSalutation(): void {
        if (this.showField('salutation')) {
            // if salutation should be shown
            if (this.contact.data.firstname) {
                // and firstname is given
                this.genderService.getGenderForName(this.contact.data.firstname).subscribe((response) => {
                    if (response === 'M') {
                      this.contact.data.salutation = 'male';
                      this.form.get('salutation').setValue(this.contact.data.salutation);
                    }
                    if (response === 'F') {
                      this.contact.data.salutation = 'female';
                      this.form.get('salutation').setValue(this.contact.data.salutation);
                    }
                    this.showSalutation();
                });
            } else if (this.showField('firstname') === false) {
                // and firstname field is not shown
                this.showSalutation();
            }
        }
    }

    /** calculate wrapper */
    showSalutation(): void {
        this.salutationShown = true;
    }

    /**
     * Checks the available checkboxes and preselects one if there is only one available
     */

    preselectCheckbox(): void {
        // checkboxes
        const checkboxes = [
            { name: 'wants_newsletter', isAvailable: this.hasNewsletterRegistration && !this.newsletterSubscriptionFinished },
            { name: 'wants_postinfo', isAvailable: this.hasPostInfo },
            { name: 'wants_donation_receipt', isAvailable: this.hasDonationReceipt },
        ];
    
        // filter for availability
        const availableCheckboxes = checkboxes.filter(checkbox => checkbox.isAvailable);
    
        // if only one checkbox is available, preselect it
        if (availableCheckboxes.length === 1) {
            const checkbox = availableCheckboxes[0];
            this.contact[checkbox.name] = true;
            this.form.controls[checkbox.name].setValue(true);
        }
    }

    /**
    * fix the email address typos
    */
    correctEmailAddress() {
        // get the email address
        const email = this.form.controls['email'].value;

        // fix the email address
        const correctedEmail = this.emailService.correctEmailAddress(email);

        // set the corrected email address
        this.form.controls['email'].setValue(correctedEmail);
        this.contact.data.email = correctedEmail;
    }
}
